// Interaction inside the app, for presence (PRD FR-PRES-03, ADR 0008).
//
// Only what the person does on this page counts: pointer, keyboard, wheel,
// touch and focus, while the page is visible. Nothing here — or anywhere — uses
// the browser's Idle Detection API or anything that observes the computer
// outside the HRMS; that would be beyond purpose P-017.
//
// The browser never decides Away. It reports activity, at most once a minute,
// and the server keeps the ten-minute clock.

/** Interaction this recent counts as "using the app" when a connection opens. */
export const RECENT_MS = 5 * 60_000;

const EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart', 'focus'] as const;

let last = 0;
const listeners = new Set<() => void>();

function seen(): void {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
  last = Date.now();
  for (const fn of listeners) fn();
}

let started = 0;

/** Start listening. Returns a function that stops; nested starts are counted. */
export function trackActivity(target: Window = window): () => void {
  if (started++ === 0) {
    for (const e of EVENTS) target.addEventListener(e, seen, { capture: true, passive: true });
  }
  return () => {
    if (--started === 0) {
      for (const e of EVENTS) target.removeEventListener(e, seen, { capture: true });
    }
  };
}

/** Whether the person used the app within the last `within` milliseconds. */
export function recentlyActive(within = RECENT_MS, now = Date.now()): boolean {
  return last > 0 && now - last < within;
}

/** Hear about each interaction. Called often; keep the listener cheap. */
export function onActivity(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** For tests: forget everything. */
export function resetActivity(): void {
  last = 0;
  listeners.clear();
}
